import React, { FC, useEffect, useState } from 'react'
import cn from 'classnames'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from 'formik'
import * as Yup from 'yup'

import { Button } from '@src/components/Button/Button'
import style from '@src/components/InfoProfileBlock/InfoProfileBlock.module.scss'
import { AppDispatch } from '@src/store/store'
import { selectUserCurrentAddresses, selectUserIsLoadingChangePhones } from '@src/store/ducks/user/selectors'
import { fetchPersonalData, savePersonalData } from '@src/store/ducks/user/thunks'
import { PersonalDataType } from '@src/api/user-api'
import { validateForm } from '@src/messages/validateForm'

const ChevronIcon = ({ isOpen }: { isOpen: boolean }) => (
  <svg
    width="12"
    height="8"
    viewBox="0 0 12 8"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }}
  >
    <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const BY_MOBILE_REGEX = /^(\+?375|80)(29|33|44|25)\d{7}$/

const toInputFormat = (dateStr: string) => {
  if (!dateStr || dateStr.length !== 10) return ''
  const [day, month, year] = dateStr.split('.')
  return `${year}-${month}-${day}`
}

const toApiFormat = (dateStr: string) => {
  if (!dateStr || dateStr.length !== 10) return ''
  const [year, month, day] = dateStr.split('-')
  return `${day}.${month}.${year}`
}

const validationSchema = Yup.object().shape({
  surname: Yup.string().required(validateForm.required),
  first_name: Yup.string().required(validateForm.required),
  doc_no: Yup.string().required(validateForm.required),
  doc_serial: Yup.string().required(validateForm.required),
})

export const PersonalDataBlock: FC<{ className?: string }> = ({ className }) => {
  const dispatch = useDispatch<AppDispatch>()
  const isLoading = useSelector(selectUserIsLoadingChangePhones)
  const personalData = useSelector((state: any) => state.user.personalData)
  const currentAccount = useSelector(selectUserCurrentAddresses)
  const contractNumber = currentAccount?.account || ''

  const [apiData, setApiData] = useState<PersonalDataType | null>(null)
  const [isAccordionOpen, setIsAccordionOpen] = useState(false)

  const formik = useFormik<PersonalDataType>({
    initialValues: {
      person_id: 0,
      second_name: '',
      first_name: '',
      surname: '',
      doc_no: '',
      doc_serial: '',
      adres_register: '',
      vc_document: '',
      document_date: '',
      document_end_date: '',
    },
    validationSchema,
    validateOnMount: true,
    onSubmit: () => {},
    enableReinitialize: false,
  })

  const isMobileContract = BY_MOBILE_REGEX.test(contractNumber.replace(/\s/g, ''))

  useEffect(() => {
    if (isMobileContract) dispatch(fetchPersonalData())
  }, [dispatch, isMobileContract])

  useEffect(() => {
    if (personalData && !formik.dirty) {
      const normalizedData = {
        person_id: personalData.personId || personalData.person_id || 0,
        second_name: personalData.secondName || personalData.second_name || '',
        first_name: personalData.firstName || personalData.first_name || '',
        surname: personalData.surname || '',
        doc_no: personalData.docNo || personalData.doc_no || '',
        doc_serial: personalData.docSerial || personalData.doc_serial || '',
        adres_register: personalData.adresRegister || personalData.adres_register || '',
        vc_document: personalData.vcDocument || personalData.vc_document || '',
        document_date: toInputFormat(personalData.documentDate || personalData.document_date || ''),
        document_end_date: toInputFormat(personalData.documentEndDate || personalData.document_end_date || ''),
      }
      if (normalizedData.person_id > 0) {
        setApiData(normalizedData)
        formik.setValues(normalizedData, false)
      }
    }
  }, [personalData])

  const handleSave = () => {
    if (!apiData) return
    const dataToSend = {
      ...apiData,
      ...formik.values,
      document_date: toApiFormat(formik.values.document_date),
      document_end_date: toApiFormat(formik.values.document_end_date),
    }
    dispatch(savePersonalData(dataToSend))
  }

  const isSaveDisabled = isLoading || !formik.isValid || !apiData?.person_id

  if (!isMobileContract) return null

  // Универсальный рендер инпута
  const renderInput = (name: keyof PersonalDataType, label: string | React.ReactNode, placeholder?: string,
    type = 'text', wrapperClass = '') => (
      <div className={cn(style.fieldWrapper, wrapperClass)}>
        <label className={style.label}>
          {label}
          <input
            type={type}
            name={name}
            placeholder={placeholder}
            value={formik.values[name] || ''}
            onChange={(e) => {
              formik.handleChange(e)
              if (!['document_date', 'document_end_date'].includes(name)) {
                setApiData((prev) => (prev ? { ...prev, [name]: e.target.value } : null))
              }
            }}
            onBlur={formik.handleBlur}
            className={cn(style.input, formik.touched[name] && formik.errors[name] ? style.error : '')}
          />
        </label>
        {formik.touched[name] && formik.errors[name] && (
        <div className={style.errorMessage}>{String(formik.errors[name])}</div>
        )}
      </div>
  )

  // @ts-ignore
  return (
    <div className={cn(style.infoBlock, style.personalDataBlock, className)}>
      <div className={style.firstBox}>
        <div className={style.title}>Персональные данные</div>

        <div className={style.topRowGrid}>
          {renderInput('surname', 'Фамилия', 'Иванов')}
          {renderInput('first_name', 'Собственное имя', 'Иван')}
          {renderInput('second_name', (
            <>
              Отчество
              <span className={style.optionalLabel}> (если таковое имеется)</span>
            </>))}
        </div>

        {renderInput('adres_register', 'Регистрация по месту жительства и (или) месту пребывания',
          undefined, 'text', style.fullWidthField)}
        {renderInput('doc_no', 'Идентификационный номер', undefined, 'text', style.fullWidthField)}

        <div className={style.accordion}>
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions,jsx-a11y/click-events-have-key-events */}
          <div className={style.accordionHeader} onClick={() => setIsAccordionOpen(!isAccordionOpen)}>
            <span>Иные данные, в случае отсутствия идентификационного номера</span>
            <ChevronIcon isOpen={isAccordionOpen} />
          </div>

          {isAccordionOpen && (
            <div className={style.accordionContent}>
              {renderInput('doc_serial', 'Серия (при наличии) номер', undefined, 'text', style.fullWidthField)}
              {renderInput('vc_document', 'Наименование или код органа, выдавшего документ',
                undefined, 'text', style.fullWidthField)}

              <div className={style.datesRow}>
                {renderInput('document_date', 'Дата выдачи', undefined, 'date')}
                {renderInput('document_end_date', 'Срок его действия', undefined, 'date')}
              </div>
            </div>
          )}
        </div>

      </div>

      <div className={style.secondBox}>
        <Button
          loading={isLoading}
          disabled={isSaveDisabled}
          htmlType="button"
          onClickFunc={handleSave}
          className={style.btn}
          type="textSecondary"
        >
          Сохранить данные
        </Button>
      </div>
    </div>
  )
}
